import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link } from "react-router-dom";
import api from "../../api/axiosInstance";
import ProfilePhoto from "../../components/ProfilePhoto";
import { useProfile } from "../../providers/ProfileProvider";

const formatLastSignIn = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
};

const AdminUserManagement = () => {
  const profile = useProfile();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get("/users/");
        setUsers(data || []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to fetch users");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
    const email = (u.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aVal = orderBy === "name"
      ? `${a.firstName} ${a.lastName}`.toLowerCase()
      : orderBy === "email"
        ? (a.email || "").toLowerCase()
        : "";
    const bVal = orderBy === "name"
      ? `${b.firstName} ${b.lastName}`.toLowerCase()
      : orderBy === "email"
        ? (b.email || "").toLowerCase()
        : "";
    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });

  const paginatedUsers = sortedUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalUsers = filteredUsers.length;

  const getInitials = (firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return "?";
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Find User
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search by name or email to change contractual status
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: 400 }}
            />
            <Box
              component="button"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "#f5f5f5",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "0.875rem",
                "&:hover": { backgroundColor: "#eee" },
              }}
            >
              <FilterAltOutlinedIcon fontSize="small" /> Filter
            </Box>
          </Box>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            All Users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage user accounts and contractual status
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
          ) : (
          <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#F5F0ED",
                    "& th": {
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleRequestSort("name")}
                    >
                      User Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "email"}
                      direction={orderBy === "email" ? order : "asc"}
                      onClick={() => handleRequestSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>User Roles</TableCell>
                  <TableCell>Contractual Status</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "lastSignIn"}
                      direction={orderBy === "lastSignIn" ? order : "asc"}
                      onClick={() => handleRequestSort("lastSignIn")}
                    >
                      Last Sign-in
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => {
                  const userRole = user.roles?.[0] || user.primaryRole || "—";
                  const contractualStatus = user.contractualStatus || "—";
                  const previousStatus = user.previousContractualStatus;
                  const detailId = user.candidateId || user._id;
                  return (
                  <TableRow
                    key={user._id}
                    sx={{
                      "&:hover": { backgroundColor: "action.hover" },
                      "& td": { borderBottom: "1px solid", borderColor: "divider" },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: "#E0E0E0",
                            color: "#616161",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(user.firstName, user.lastName)}
                        </Avatar>
                        <Link
                          to={`/userinfo/${detailId}`}
                          style={{
                            color: "#1976d2",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          {user.firstName} {user.lastName}
                        </Link>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{userRole}</TableCell>
                    <TableCell>
                      <Box>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: "#E0E0E0",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                          }}
                        >
                          {contractualStatus}
                        </Box>
                        {previousStatus && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ color: "text.secondary", mt: 0.5 }}
                          >
                            Previously: {previousStatus}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatLastSignIn(user.lastSignIn)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        component={Link}
                        to={`/userinfo/${detailId}`}
                        sx={{ color: "text.secondary" }}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
            }}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
            labelRowsPerPage="Rows per page:"
          />
          </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminUserManagement;
